import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { HorizontalFilterBar } from '../../Listings/Filters/HorizontalFilterBar';
import { AddressSearch } from '../../Filters/AddressSearch';
import { europeanCountries } from '../../../lib/constants/countries';

interface Filters {
  jobTypes: string[];
  countries: string[];
}

interface JobFiltersProps {
  filters: Filters;
  onFilterChange: (filterType: keyof Filters, values: string[]) => void;
  onAddressSelect?: (latitude: number, longitude: number, address: string) => void;
  showExpanded?: boolean;
}

export function JobFilters({ filters, onFilterChange, onAddressSelect, showExpanded = true }: JobFiltersProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(showExpanded);

  const jobTypes = [
    { value: 'full_time', label: t('filters.fullTime') },
    { value: 'part_time', label: t('filters.partTime') },
    { value: 'contract', label: t('filters.contract') },
    { value: 'freelance', label: t('filters.freelance') },
    { value: 'volunteer', label: t('filters.volunteer') },
    { value: 'internship', label: t('filters.internship') }
  ];
  const hasActiveFilters = filters.jobTypes.length > 0 || filters.countries.length > 0;

  const clearAllFilters = () => {
    onFilterChange('jobTypes', []);
    onFilterChange('countries', []);
  };

  const filterGroups = [
    {
      label: t('filters.jobType'),
      options: jobTypes,
      selectedValues: filters.jobTypes,
      onChange: (values: string[]) => onFilterChange('jobTypes', values),
      multiSelect: true
    },
    {
      label: t('filters.country'),
      options: europeanCountries,
      selectedValues: filters.countries,
      onChange: (values: string[]) => onFilterChange('countries', values),
      multiSelect: true
    }
  ];

  return (
    <div className="border-b border-gray-200 w-full">
      <div className="px-3 sm:px-4 lg:px-6 max-w-page mx-auto">
        {/* Mobile: compact filter button */}
        <div className="lg:hidden flex items-center gap-2 py-2">
          <HorizontalFilterBar
            filterGroups={filterGroups}
            onClearAll={clearAllFilters}
            hasActiveFilters={hasActiveFilters}
            inline={true}
          />
        </div>

        {/* Desktop: full horizontal row */}
        <div className="hidden lg:block">
          <div className="w-full overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 sm:gap-2 py-2 min-w-max">
              <HorizontalFilterBar
                filterGroups={filterGroups}
                onClearAll={clearAllFilters}
                hasActiveFilters={hasActiveFilters}
                inline={true}
              />

              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex-shrink-0 h-9 px-3 rounded-full gap-2 inline-flex items-center text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground"
              >
                {isExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('filters.less')}</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('filters.more')}</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {isExpanded && onAddressSelect && (
            <div className="pt-4 pb-4 border-t border-gray-200 mt-2">
              <div className="max-w-md">
                <AddressSearch onAddressSelect={onAddressSelect} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
